import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { CounterPartyService } from './counter-party.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-counter-party',
    templateUrl: './counter-party.component.html',
    styleUrls: ['./counter-party.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CounterPartyComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  headerData: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  minimise = false;
  currentTabName = 'Form Entry';
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  modalTitle = 'Add/Edit Counter Party Details';
  selectedEntity = '';
  selectedSource = '';
  selectedLineNo = '';
  selectedColumn = '';
  lineOptions: string[] = [];
  columnList: string[] = ['A', 'B', 'C'];
  entityDetails: any[] = [];
  sourceDetails: any[] = [];
  countryDetails: any[] = [];
  cpSubFormList: any[] = [];
  cpFormRows: any[] = [];
  cpFormColumns: string[] = [];
  selectedSystemAcct: any = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private counterPartyService: CounterPartyService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.headerData = header;
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);

    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || !this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    this.loadInitialLists();
    if (this.rowData?.COMBINATION_KEY) {
      this.loadForExistingRow();
    } else {
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private loadInitialLists(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    const ssoId = this.USER_SETTINGS?.user?.sso_id || '';
    if (!baseUrl || !ssoId) return;
    this.counterPartyService.getEntityDetails(baseUrl, ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.entityDetails = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
      this.cdr.detectChanges();
    });
    this.counterPartyService.getSourceDetails(baseUrl, ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.sourceDetails = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
      this.cdr.detectChanges();
    });
    this.counterPartyService.getCountryDetails(baseUrl, ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.countryDetails = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
      this.cdr.detectChanges();
    });
  }

  onSourceChange(): void {
    if (this.selectedSource === 'F8916A-PIII') {
      this.lineOptions = ['1', '2', '3a', '3b', '4'];
    } else if (this.selectedSource === 'F8916A-PII') {
      this.lineOptions = ['2', '3', '4a', '4b', '5'];
    } else {
      this.lineOptions = [];
      this.selectedLineNo = '';
    }
    this.cpSubFormList = [];
    this.cpFormRows = [];
    this.cpFormColumns = [];
    this.cdr.detectChanges();
  }

  onSelectionReady(): void {
    if (!this.selectedEntity || !this.selectedSource || !this.selectedColumn) return;
    const entity = this.entityDetails.find((x: any) => x.ENTITY_DETAILS === this.selectedEntity);
    const sourceSplit = this.selectedSource.split('-');
    const combinationKey = entity?.COMBINATION_KEY;
    if (!combinationKey) return;
    const scheduleName = sourceSplit[1] || '';
    const cpSchedule = scheduleName === 'PII' ? 'INTINC' : 'INTEXP';
    const paramsBase = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      combination_key: combinationKey,
      cp_form_name: 'CPFORM',
      cp_schedule_name: cpSchedule,
      line_no: this.selectedLineNo,
      col_id: this.selectedColumn,
      form_no: sourceSplit[0] || '',
      schedule: scheduleName,
      part_no: 'NA'
    };
    this.counterPartyService.getCPSubFormDetails(this.GENERAL_CONFIG.base_url, paramsBase).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.cpSubFormList = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
      this.showFormLoader = false;
      this.cdr.detectChanges();
    });
  }

  selectSubFormRow(row: any): void {
    this.selectedSystemAcct = row?.ACCOUNT_KEY || 0;
    this.loadCounterPartyForm(this.selectedSystemAcct);
  }

  private loadForExistingRow(): void {
    const row = this.rowData || {};
    const cpSchedule = ((row.FORM === 'F1120' || row.FORM === '1120PC' || row.FORM === '1120L' || row.FORM === '1065') && row.SCHEDULE === 'M3')
      ? (row.LINE_NO === '1' ? 'M3P2L1' : 'M3P2OTHER')
      : (row.SCHEDULE === 'PII' ? 'INTINC' : 'INTEXP');
    const paramsBase = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      combination_key: row.COMBINATION_KEY,
      cp_form_name: 'CPFORM',
      cp_schedule_name: cpSchedule,
      line_no: row.LINE_NO,
      col_id: row.COL_ID || row.col_id || 'A',
      form_no: row.FORM,
      schedule: row.SCHEDULE,
      part_no: row.PART_NO || 'NA'
    };
    this.counterPartyService.getCPSubFormDetails(this.GENERAL_CONFIG.base_url, paramsBase).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.cpSubFormList = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
      const firstDataRow = this.cpSubFormList.find((x: any) => x.ROW_TYPE === 'DATA_ROW');
      this.selectedSystemAcct = firstDataRow?.ACCOUNT_KEY || 0;
      this.loadCounterPartyForm(this.selectedSystemAcct);
    });
  }

  private loadCounterPartyForm(systemAcct: any): void {
    const row = this.rowData || {};
    const cpSchedule = ((row.FORM === 'F1120' || row.FORM === '1120PC' || row.FORM === '1120L' || row.FORM === '1065') && row.SCHEDULE === 'M3')
      ? (row.LINE_NO === '1' ? 'M3P2L1' : 'M3P2OTHER')
      : (row.SCHEDULE === 'PII' ? 'INTINC' : 'INTEXP');
    const params = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      combination_key: row.COMBINATION_KEY || 0,
      cp_form_name: 'CPFORM',
      cp_schedule_name: cpSchedule,
      line_no: row.LINE_NO || this.selectedLineNo,
      col_id: row.COL_ID || row.col_id || this.selectedColumn || 'A',
      form_no: row.FORM || (this.selectedSource.split('-')[0] || ''),
      schedule: row.SCHEDULE || (this.selectedSource.split('-')[1] || ''),
      part_no: row.PART_NO || 'NA',
      sys_acct_selected: systemAcct || 0
    };
    this.counterPartyService.getCPFormDetails(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.cpFormRows = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
      this.cpFormColumns = this.cpFormRows.length > 0 ? Object.keys(this.cpFormRows[0]) : [];
      this.showFormLoader = false;
      this.cdr.detectChanges();
    });
  }

  save() {
    if (this.crudLoading || this.isSaveClicked) return;
    if (this.cpFormRows.length === 0) {
      this.gtwSnackbarService.openSnackBar('No counter party data to save', 'gtw-snackbar--yellow');
      return;
    }
    this.crudLoading = true;
    this.isSaveClicked = true;
    this.cdr.detectChanges();
    const settings = [{
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      screen_key: this.globalService?.screen_key || this.globalService?.globalParams?.screen_key
    }];
    const params: any = {
      json_data: JSON.stringify(this.cpFormRows),
      json_settings: JSON.stringify(settings),
      process_name: 'Counter-Party-Modal-Save',
      cp_form_name: 'CPFORM',
      cp_schd_name: this.rowData?.SCHEDULE === 'PII' ? 'INTINC' : 'INTEXP',
      form_selected: this.rowData?.FORM || '',
      schd_selected: this.rowData?.SCHEDULE || '',
      part_selected: this.rowData?.PART_NO || 'NA',
      line_selected: this.rowData?.LINE_NO || this.selectedLineNo || '',
      col_selected: this.rowData?.COL_ID || this.selectedColumn || 'A',
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY
    };
    this.counterPartyService.saveCPForm(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Counter Party Details are successfully updated.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.cpSubFormList = [];
    this.cpFormRows = [];
    this.cpFormColumns = [];
    this.selectedSystemAcct = null;
    if (this.rowData?.COMBINATION_KEY) {
      this.loadForExistingRow();
    } else {
      this.onSelectionReady();
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
